import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { useCreateCategoryMutation, useUpdateCategoryMutation } from "@/hooks/useCategoryQueries";
import { cn } from "@/lib/utils";
import { TemplateCategory } from "@/services/supabase/templates";
import {
    Anchor, Axe, Backpack, BookOpen, Box, Briefcase, Building, Calendar, Castle, Cloud,
    Coins, Compass, Cpu, Crown, Dna, Droplets, Feather, Flag, Flame, FlaskConical,
    Gem, Ghost, GraduationCap, Hammer, Heart, Home, Key, Landmark, Layers, Lightbulb,
    Lock, Map, MapPin, MessageSquare, Moon, Mountain, Music, PawPrint, Radio, Rocket,
    Scroll, Shield, Skull, Star, Sun, Sword, Tent, TreeDeciduous, User, Users,
    Wand2, Warehouse, Zap
} from "lucide-react";
import React, { useEffect, useState } from "react";

// Icon Selection List
const AVAILABLE_ICONS = [
    // General
    { name: 'User', icon: User },
    { name: 'Users', icon: Users },
    { name: 'MapPin', icon: MapPin },
    { name: 'Box', icon: Box },
    { name: 'Layers', icon: Layers },
    // Nature
    { name: 'Mountain', icon: Mountain },
    { name: 'TreeDeciduous', icon: TreeDeciduous },
    { name: 'Cloud', icon: Cloud },
    { name: 'Sun', icon: Sun },
    { name: 'Moon', icon: Moon },
    { name: 'Flame', icon: Flame },
    { name: 'Droplets', icon: Droplets },
    // Structures
    { name: 'Castle', icon: Castle },
    { name: 'Home', icon: Home },
    { name: 'Building', icon: Building },
    { name: 'Landmark', icon: Landmark },
    { name: 'Tent', icon: Tent },
    { name: 'Warehouse', icon: Warehouse },
    // Items & Economy
    { name: 'Coins', icon: Coins },
    { name: 'Gem', icon: Gem },
    { name: 'Backpack', icon: Backpack },
    { name: 'Compass', icon: Compass },
    { name: 'Map', icon: Map },
    { name: 'Key', icon: Key },
    { name: 'Lock', icon: Lock },
    { name: 'BookOpen', icon: BookOpen },
    { name: 'Scroll', icon: Scroll },
    { name: 'Feather', icon: Feather },
    // Magic & Combat
    { name: 'Wand2', icon: Wand2 },
    { name: 'Skull', icon: Skull },
    { name: 'Sword', icon: Sword },
    { name: 'Shield', icon: Shield },
    { name: 'FlaskConical', icon: FlaskConical },
    { name: 'Ghost', icon: Ghost },
    { name: 'Crown', icon: Crown },
    // Sci-Fi & Tech
    { name: 'Rocket', icon: Rocket },
    { name: 'Cpu', icon: Cpu },
    { name: 'Dna', icon: Dna },
    { name: 'Radio', icon: Radio },
    { name: 'Zap', icon: Zap },
    { name: 'Lightbulb', icon: Lightbulb },
    // Actions & Misc
    { name: 'Hammer', icon: Hammer },
    { name: 'Axe', icon: Axe },
    { name: 'Anchor', icon: Anchor },
    { name: 'Briefcase', icon: Briefcase },
    { name: 'GraduationCap', icon: GraduationCap },
    { name: 'MessageSquare', icon: MessageSquare },
    { name: 'Calendar', icon: Calendar },
    { name: 'Music', icon: Music },
    { name: 'PawPrint', icon: PawPrint },
    { name: 'Star', icon: Star },
    { name: 'Heart', icon: Heart },
    { name: 'Flag', icon: Flag },
];

interface CategoryDialogProps {
    isOpen: boolean;
    onClose: () => void;
    mode: 'create' | 'edit';
    category?: TemplateCategory;
    projectId: number;
}

export function CategoryDialog({
    isOpen,
    onClose,
    mode,
    category,
    projectId
}: CategoryDialogProps) {
    const { toast } = useToast();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [selectedIcon, setSelectedIcon] = useState("Layers");

    const createMutation = useCreateCategoryMutation();
    const updateMutation = useUpdateCategoryMutation();

    const isSubmitting = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (isOpen) {
            if (mode === 'edit' && category) {
                setName(category.name);
                setDescription(category.description || "");
                // If category has icon, use it, else default or infer
                setSelectedIcon(category.icon || "Layers");
            } else {
                setName("");
                setDescription("");
                setSelectedIcon("Layers");
            }
        }
    }, [isOpen, mode, category]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            toast({
                title: "Validation Error",
                description: "Category name is required.",
                variant: "destructive",
            });
            return;
        }

        try {
            if (mode === 'create') {
                await createMutation.mutateAsync({
                    name,
                    description: description || undefined,
                    icon: selectedIcon,
                    project_id: projectId,
                });
                toast({
                    title: "Category Created",
                    description: `Category "${name}" created successfully.`,
                });
            } else if (mode === 'edit' && category) {
                await updateMutation.mutateAsync({
                    categoryId: category.id,
                    data: {
                        name,
                        description: description || undefined,
                        icon: selectedIcon,
                        project_id: projectId,
                    }
                });
                toast({
                    title: "Category Updated",
                    description: `Category "${name}" updated successfully.`,
                });
            }
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "Error",
                description: `Failed to ${mode} category. Please try again.`,
                variant: "destructive",
            });
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[850px] sm:rounded-2xl p-0 overflow-hidden gap-0 border-border/60 shadow-2xl">
                <DialogHeader className="p-8 pb-6 border-b border-border/40 bg-muted/5 space-y-1">
                    <DialogTitle className="text-2xl font-semibold tracking-tight text-foreground">
                        {mode === 'create' ? 'Create New Element' : 'Edit Element'}
                    </DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground leading-relaxed">
                        {mode === 'create'
                            ? 'Define a new type of element for your world building.'
                            : 'Update the details and icon for this element type.'}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 p-8">
                        {/* Left Column: Form Inputs */}
                        <div className="space-y-6">
                            <div className="space-y-2.5">
                                <Label htmlFor="name" className="text-sm font-medium text-foreground/80 ml-1">
                                    Name
                                </Label>
                                <Input
                                    id="name"
                                    placeholder="e.g. Characters, Ancient Ruins, Magic Items"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="h-11 rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm"
                                />
                            </div>

                            <div className="space-y-2.5">
                                <Label htmlFor="description" className="text-sm font-medium text-foreground/80 ml-1">
                                    Description
                                </Label>
                                <Textarea
                                    id="description"
                                    placeholder="Describe what kind of content this element type will obtain..."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    className="min-h-[290px] rounded-xl bg-muted/30 border-muted-foreground/10 focus:border-primary/20 focus:ring-primary/20 resize-none transition-all font-medium text-foreground placeholder:text-muted-foreground/50 shadow-sm leading-relaxed"
                                />
                            </div>
                        </div>

                        {/* Right Column: Icon Picker */}
                        <div className="space-y-2.5 flex flex-col">
                            <Label className="text-sm font-medium text-foreground/80 ml-1">
                                Select Icon
                            </Label>
                            <div className="h-[400px] rounded-xl bg-muted/30 border border-muted-foreground/10 p-4 shadow-sm overflow-hidden flex flex-col">
                                <div className="flex-1 overflow-y-auto scrollbar-hide pr-2">
                                    <div className="grid grid-cols-5 gap-3">
                                        {AVAILABLE_ICONS.map((item) => {
                                            const Icon = item.icon;
                                            const isSelected = selectedIcon === item.name;
                                            return (
                                                <button
                                                    key={item.name}
                                                    type="button"
                                                    onClick={() => setSelectedIcon(item.name)}
                                                    className={cn(
                                                        "aspect-square rounded-xl flex items-center justify-center transition-all duration-200 group relative",
                                                        isSelected
                                                            ? "bg-primary text-primary-foreground shadow-md scale-100 ring-2 ring-primary ring-offset-2 ring-offset-background"
                                                            : "bg-background/50 border border-border/40 text-muted-foreground hover:bg-background hover:text-foreground hover:border-primary/30 hover:shadow-sm"
                                                    )}
                                                    title={item.name}
                                                >
                                                    <Icon className={cn(
                                                        "h-5 w-5 transition-transform duration-200",
                                                        isSelected ? "scale-110" : "group-hover:scale-110"
                                                    )} />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 bg-muted/5 border-t border-border/40 flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="min-w-[100px] rounded-xl hover:bg-muted/50"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className={cn(
                                "min-w-[140px] rounded-xl font-medium shadow-sm active:scale-95 transition-all text-base",
                                isSubmitting && "opacity-80"
                            )}
                        >
                            {mode === 'create' ? 'Create Element' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}



